import { storage } from '@forge/api'
import type {
  Item,
  ItemRelease,
  RadarData,
  RadarDataStorage,
  RequestContext
} from '../types'
import { track } from './posthog'
import {
  CURRENT_SCHEMA_VERSION,
  ItemSchema,
  RadarDataSchema,
  RadarDataStorageSchema
} from './schema/zodSchemas'
import { isLicensed } from './stripeHandler'
import { getRadarDataKey, MAX_UNLICENSED_ITEMS } from './techradarHandler'

const isItem = (item: Item): item is Item => {
  const result = ItemSchema.safeParse(item)
  return result.success
}

// Helper function to validate RadarData
function validateRadarData(data: RadarData): RadarData {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error(`RadarData is not a valid object: ${JSON.stringify(data)}`)
  }
  const result = RadarDataSchema.safeParse(data)
  if (!result.success) {
    throw new Error(
      `RadarData validation failed: ${JSON.stringify(result.error.format())}`
    )
  }
  return result.data
}

// Helper function to check if adding a new item would exceed the limit
export const checkLicenseLimit = async (
  context: RequestContext,
  radarId: number,
  isImport = false,
  importCount = 0
): Promise<{ canAdd: boolean; currentCount: number; maxItems: number }> => {
  const storageData = (await storage.get(getRadarDataKey(radarId))) as
    | RadarDataStorage
    | undefined

  const currentData = validateRadarData(storageData?.data || { items: [] })
  const currentCount = currentData.items.length
  const isLicensedUser = await isLicensed(context)

  // For imports, check if the total count would exceed the limit
  // For individual items, check if adding one more would exceed the limit
  const canAdd = isImport
    ? isLicensedUser || currentCount + importCount <= MAX_UNLICENSED_ITEMS
    : isLicensedUser || currentCount < MAX_UNLICENSED_ITEMS

  return {
    canAdd,
    currentCount,
    maxItems: MAX_UNLICENSED_ITEMS
  }
}

// Helper to update radar data in storage
async function updateRadarDataInStorage({
  radarId,
  updateFn,
  trackEvent
}: {
  context: RequestContext
  radarId?: number
  updateFn: (data: RadarData) => Promise<RadarData> | RadarData
  trackEvent?: (data: RadarData) => void
}): Promise<RadarData> {
  const storageKey = getRadarDataKey(radarId ?? '')
  const storageData = (await storage.get(storageKey)) as
    | RadarDataStorage
    | undefined

  const currentData = validateRadarData(storageData?.data || { items: [] })

  const newData = currentData && (await updateFn(currentData))

  const newDataStore = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    data: newData
  }

  RadarDataStorageSchema.parse(newDataStore)
  await storage.set(storageKey, newDataStore)

  if (trackEvent) {
    if (newData) {
      trackEvent(newData)
    }
  }

  return newData || currentData || { items: [] }
}

// --- Item/Release manipulation helpers ---
function updateItemById(
  items: Item[],
  itemId: string,
  updateFn: (item: Item) => Item
): Item[] {
  return items.map(item =>
    isItem(item) && item.id === itemId ? updateFn(item) : item
  )
}

function addReleaseToItem(item: Item, release: ItemRelease): Item {
  return { ...item, releases: [...item.releases, release] }
}

function updateReleaseInItem(item: Item, release: ItemRelease): Item {
  return {
    ...item,
    releases: item.releases.map(r => (r.id === release.id ? release : r))
  }
}

function removeReleaseFromItem(item: Item, releaseId: string): Item {
  return {
    ...item,
    releases: item.releases.filter(r => r.id !== releaseId)
  }
}

function removeItemById(items: Item[], itemId: string): Item[] {
  return items.filter(item => !(isItem(item) && item.id === itemId))
}
// --- End helpers ---

export const updateItemProperties = async (
  context: RequestContext,
  itemId: string,
  quadrant: string,
  tags: string[],
  archived: boolean,
  radarId: number
): Promise<RadarData> => {
  return updateRadarDataInStorage({
    context,
    radarId,
    updateFn: currentData => ({
      ...currentData,
      items: updateItemById(currentData.items, itemId, item => ({
        ...item,
        quadrant,
        tags,
        archived
      }))
    }),
    trackEvent: () => {
      track(context, 'admin:item_update', {
        item_id: itemId,
        quadrant,
        tags_count: tags?.length,
        is_archived: archived,
        radar_id: radarId
      })
    }
  })
}

export const updateRelease = async (
  context: RequestContext,
  itemId: string,
  release: ItemRelease,
  radarId: number
): Promise<RadarData> => {
  return updateRadarDataInStorage({
    context,
    radarId,
    updateFn: currentData => ({
      ...currentData,
      items: updateItemById(currentData.items, itemId, item =>
        updateReleaseInItem(item, release)
      )
    }),
    trackEvent: () => {
      track(context, 'admin:release_update', {
        item_id: itemId,
        release_id: release.id,
        radar_id: radarId
      })
    }
  })
}

export const addRelease = async (
  context: RequestContext,
  itemId: string,
  release: Partial<ItemRelease> & Omit<ItemRelease, 'id'>,
  radarId: number
): Promise<RadarData> => {
  const fullRelease: ItemRelease = {
    ...release,
    id: release.id || crypto.randomUUID()
  } as ItemRelease
  return updateRadarDataInStorage({
    context,
    radarId,
    updateFn: currentData => ({
      ...currentData,
      items: updateItemById(currentData.items, itemId, item =>
        addReleaseToItem(item, fullRelease)
      )
    }),
    trackEvent: () => {
      track(context, 'admin:release_create', {
        item_id: itemId,
        radar_id: radarId
      })
    }
  })
}

export const deleteRelease = async (
  context: RequestContext,
  itemId: string,
  releaseId: string,
  radarId: number
): Promise<RadarData> => {
  return updateRadarDataInStorage({
    context,
    radarId,
    updateFn: currentData => ({
      ...currentData,
      items: updateItemById(currentData.items, itemId, item =>
        removeReleaseFromItem(item, releaseId)
      )
    }),
    trackEvent: () => {
      track(context, 'admin:release_delete', {
        item_id: itemId,
        release_id: releaseId,
        radar_id: radarId
      })
    }
  })
}

export const deleteItem = async (
  context: RequestContext,
  itemId: string,
  radarId: number
): Promise<RadarData> => {
  return updateRadarDataInStorage({
    context,
    radarId,
    updateFn: currentData => ({
      ...currentData,
      items: removeItemById(currentData.items, itemId)
    }),
    trackEvent: () => {
      track(context, 'admin:item_delete', {
        item_id: itemId,
        radar_id: radarId
      })
    }
  })
}

export const addItem = async (
  context: RequestContext,
  item: Item,
  radarId: number
): Promise<RadarData> => {
  // Check license limit before proceeding
  const { canAdd, maxItems } = await checkLicenseLimit(context, radarId)
  if (!canAdd) {
    throw new Error(
      `Unlicensed version is limited to ${maxItems} radar items. Please upgrade to add more items.`
    )
  }
  return updateRadarDataInStorage({
    context,
    radarId,
    updateFn: async currentData => {
      if (
        currentData.items.length >= maxItems &&
        !(await isLicensed(context))
      ) {
        throw new Error(
          `Unlicensed version is limited to ${maxItems} radar items. Please upgrade to add more items.`
        )
      }
      return {
        ...currentData,
        items: [...currentData.items, item]
      }
    },
    trackEvent: () => {
      track(context, 'admin:item_create', {
        item_id: item.id,
        radar_id: radarId
      })
    }
  })
}
